import type { SelectOption } from '@aus-platform/design-system';
import {
  ColorClass,
  Icon,
  IconIdentifier,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
// TODO: Move WorkspaceLayer type to src/map-3d/shared/types.
import type { WorkspaceLayer } from 'src/map-3d/components/map-3d-sidebar/shared';
import { TooltipDetails } from 'src/shared/components';
import { isEllipsis } from 'src/shared/helpers';

export type PolygonDatasetDropdownItemProps = {
  option: SelectOption<WorkspaceLayer>;
  isSelected: boolean;
  handleOrthoChange: (orthoLayer: WorkspaceLayer) => void;
};

export const PolygonDatasetDropdownItem: React.FC<
  PolygonDatasetDropdownItemProps
> = ({ option, isSelected, handleOrthoChange }) => {
  // States.
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Refs.
  const layerRef = useRef<HTMLSpanElement>(null);
  const pathRef = useRef<HTMLSpanElement>(null);

  // Constants.
  const layer = option.value;
  const parentDescription = `${layer.site.name} > ${layer.iteration.name}`;

  // Effects.
  useLayoutEffect(() => {
    const requiresTooltip = isEllipsis(layerRef) || isEllipsis(pathRef);
    if (requiresTooltip !== isOverflowing) {
      setIsOverflowing(requiresTooltip);
    }
  }, [layer.name, layer.site.name, layer.iteration.name]);

  // Renderers.
  const renderContent = () => (
    <div className="polygon-dataset-dropdown__item-row">
      <div className="polygon-dataset-dropdown__item-main">
        <div className="polygon-dataset-dropdown__item-inner">
          <span
            ref={layerRef}
            className="polygon-dataset-dropdown__item-line polygon-dataset-dropdown__item-line--primary"
          >
            {layer.name}
          </span>
          <span
            ref={pathRef}
            className="polygon-dataset-dropdown__item-line polygon-dataset-dropdown__item-line--secondary"
          >
            {parentDescription}
          </span>
        </div>
      </div>
      {isSelected && (
        <span aria-hidden className="polygon-dataset-dropdown__item-check">
          <Icon
            colorClass={ColorClass.White}
            identifier={IconIdentifier.CheckSmall}
            size={18}
          />
        </span>
      )}
    </div>
  );

  return (
    <Tooltip
      placement={Placement.Right}
      className="polygon-dataset-dropdown__item__tooltip"
      show={isOverflowing ? undefined : false}
      hoverText={
        <TooltipDetails
          items={[
            { label: 'Layer', value: layer.name },
            { label: 'Site', value: layer.site.name },
            { label: 'Iteration', value: layer.iteration.name },
          ]}
        />
      }
    >
      <Dropdown.Item
        active={isSelected}
        className="polygon-dataset-dropdown__item__container"
        onClick={() => {
          handleOrthoChange(layer);
        }}
      >
        {renderContent()}
      </Dropdown.Item>
    </Tooltip>
  );
};
